import dayjs from "dayjs";
import _sortBy from "lodash.sortby";
import { FranchiseeItemFragment } from "../gql/graphql.ts";
import { TeacherSlotFilter } from "./getTeacherSlotStats.ts";
import { AvailableTeacherSlotDto, BookedClassRoomDto } from "./types.ts";

type TeacherSlotStatus = "Assigned" | "Candidate" | "Available";

type TeacherInvolvement = {
  teacherId: string;
  status: TeacherSlotStatus;
};

export type BookedClassRoomsCsvRow = {
  weekStart: string;
  date: string;
  startTime: string;
  endTime: string;
  teacher: string;
  teacherEmail: string;
  status: TeacherSlotStatus;
  classType: string;
  userGroup: string;
  classRoomId: string;
};

const dateFormat = "YYYY-MM-DD";
const timeFormat = "HH:mm";

function findTeacherInvolvedInClassRoom(
  classRoom: BookedClassRoomDto,
  teacherFilter?: TeacherSlotFilter,
): TeacherInvolvement | null {
  if (!teacherFilter)
    return { teacherId: classRoom.teacherId, status: "Assigned" };

  const { teacherId, candidateClassRoomIds } = teacherFilter;
  if (classRoom.teacherId === teacherId)
    return { teacherId, status: "Assigned" };
  if (candidateClassRoomIds?.has(classRoom.classRoomId))
    return { teacherId, status: "Candidate" };
  return null;
}

function isTeacherIncludedInFilter(
  teacherId: string,
  teacherFilter?: TeacherSlotFilter,
): boolean {
  return !teacherFilter || teacherFilter.teacherId === teacherId;
}

function describeTimeRange({
  startUtc,
  endUtc,
}: {
  startUtc: string;
  endUtc: string;
}) {
  const start = dayjs(startUtc);
  return {
    weekStart: start.startOf("week").format(dateFormat),
    date: start.format(dateFormat),
    startTime: start.format(timeFormat),
    endTime: dayjs(endUtc).format(timeFormat),
  };
}

export function createBookedClassRoomsCsvRows({
  classRooms,
  availableSlots,
  teacherFilter,
  teacherById,
  getUserGroupName,
}: {
  classRooms: BookedClassRoomDto[];
  availableSlots: AvailableTeacherSlotDto[];
  teacherFilter?: TeacherSlotFilter;
  teacherById: Map<string, FranchiseeItemFragment>;
  getUserGroupName: (userGroupId: string) => string;
}): BookedClassRoomsCsvRow[] {
  function describeTeacher(teacherId: string) {
    const teacher = teacherById.get(teacherId);
    return {
      teacher: teacher
        ? `${teacher.givenName} ${teacher.familyName}`
        : teacherId,
      teacherEmail: teacher?.email ?? "",
    };
  }

  const classRoomRows = classRooms.flatMap(
    (classRoom): BookedClassRoomsCsvRow[] => {
      const involvement = findTeacherInvolvedInClassRoom(
        classRoom,
        teacherFilter,
      );
      if (!involvement) return [];
      return [
        {
          ...describeTimeRange(classRoom),
          ...describeTeacher(involvement.teacherId),
          status: involvement.status,
          classType: classRoom.is1on1 ? "1-to-1" : "Group",
          userGroup: getUserGroupName(classRoom.userGroupId),
          classRoomId: classRoom.classRoomId,
        },
      ];
    },
  );

  const availabilityRows = availableSlots.flatMap((slot) =>
    slot.teacherIds
      .filter((teacherId) =>
        isTeacherIncludedInFilter(teacherId, teacherFilter),
      )
      .map(
        (teacherId): BookedClassRoomsCsvRow => ({
          ...describeTimeRange(slot),
          ...describeTeacher(teacherId),
          status: "Available",
          classType: "",
          userGroup: "",
          classRoomId: "",
        }),
      ),
  );

  return _sortBy(
    [...classRoomRows, ...availabilityRows],
    ["date", "startTime", "teacher"],
  );
}
